import type { ReactNode } from "react";
import { FeatureFlags } from "./constants/feature_flags.ts";
import { Permissions as PermissionsMap } from "./constants/permissions.ts";
import { getSafeFileMimeType, getSafeFileName } from "./logic/files/index.ts";
import { getQuestionAnswerPairsBySection } from "./logic/forms/details.ts";
import type {
  ConditionFunctionTypes,
  QuestionConditionType,
  WatchedFields,
} from "./logic/forms/index.ts";
import {
  areConditionsSatisfied,
  CalculatedFieldService,
  ConditionFunctions,
  ConditionOperators,
  DateTimeSubstitutions,
  findAnswerForQuestion,
  findSectionUsingName,
  FormCalculatedFieldService,
  getAnswersForSubmission,
  getErrorCount,
  getParentQuestionName,
  getPickListValuesWithConditionalOverrides,
  getSmartDefaultValues,
  getValueForQuestionInputFromQuestionValue,
  getValuePathFromQuestionPath,
  resolveQuestionConfiguration,
  useFieldNamesToDisable,
  useGenerateFieldsToWatch,
  useGenerateLookupQueries,
  useProcessAttributeLookupQueries,
  useProcessCalculatedFields,
  useProcessConditionalOverrides,
  useResolvedFormSchema,
  useUserChangedFieldPaths,
  useValidateLookupFieldValue,
} from "./logic/forms/index.ts";
import { getNumberOfUsersThatCanAccessQuicksightConsole } from "./logic/insights/index.ts";
import {
  getDisplayedUnit,
  getPreciseUnit,
  NON_DISPLAY_UNITS,
  NON_PRECISION_UNITS,
} from "./logic/measurements/index.ts";
import type {
  AuthQueryOperatorType,
  AuthQueryType,
} from "./logic/permissions/index.ts";
import {
  AuthQueryOperator,
  createAuthorizationEvaluator,
} from "./logic/permissions/index.ts";
import { sortReportingGroupsByPriority } from "./logic/reporting-groups/index.ts";
import { getResourceIdForResource } from "./logic/templated-configurations/index.ts";
import { selectFirstAndLastTransactionState } from "./logic/transactions/index.ts";
import {
  flattenWorkflowSteps,
  getAllWorkflowSteps,
  validateAllStepIdsAreUnique,
} from "./logic/workflows/index.ts";
import { isValidereInternalUserEmail } from "./logic/users/index.ts";
import { normalizeError, usePrevious } from "./utilities.ts";
import {
  ActivityActionOptions,
  ActivityResourceTypeOptions,
} from "./view/activities/index.ts";
import { AlertOperatorOptions } from "./view/alerts/index.ts";
import {
  AssetGroupStatusOptions,
  NetworkMeasurementTypeOptions,
} from "./view/asset-groups/index.ts";
import type { AssetTypeSelectionType } from "./view/assets/index.ts";
import { AssetTypeOptions, AssetTypeSelection } from "./view/assets/index.ts";
import { DeviceStatusOptions } from "./view/devices/index.ts";
import { EquipmentStatusOptions } from "./view/equipment/index.ts";
import { EventStatusOptions } from "./view/events/display.ts";
import { FacilityStatusOptions } from "./view/facilities/index.ts";
import {
  ApprovalDecisionDisplayLabel,
  ApprovalDecisionOptions,
  getApprovalDecisionDisplayLabel,
  getInvestigationStatusDisplayLabel,
  InvestigationStatusLabel,
  InvestigationStatusOptions,
  PendingApprovalCardDefaultApproveLabel,
  PendingApprovalCardDefaultHeading,
  PendingApprovalCardDefaultRejectLabel,
  ToolInProgressTickerLabel,
  PendingApprovalCardUnknownPayloadMessage,
  InvestigationWriteApprovalDecisionUnavailableMessage,
  UnknownApprovalDecisionDisplayKey,
} from "./view/investigations/index.ts";
import { FlowStatusOptions } from "./view/flows/index.ts";
import type {
  FormSubmissionFormControllerType,
  QuestionRendererPropType,
  QuestionRenderFunctionType,
  SectionRendererPropType,
  SectionRenderFunctionType,
} from "./view/forms/index.ts";
import {
  CalculatedFieldEquationController,
  type CalculatedFieldEquationControllerProps,
  FormSubmissionFormController,
  FormSubmissionsSectionRepeater,
  FormSubmissionStatusOptions,
} from "./view/forms/index.ts";
import { InvestigationSearchStatusOptions } from "./view/investigations/index.ts";
import {
  MeasurementAggregatorOptions,
  PollutantsEmitted,
  PollutantsEmittedOptions,
} from "./view/measurements/index.ts";
import { ReportGeneratedStatusOptions } from "./view/reports/display.ts";
import { convertMapToOpts } from "./view/shared/index.ts";
import { getNameFormatReadable } from "./view/templated-reports/index.ts";
import type { DataSetSubmissionTabularDataRowType } from "./view/transactions/index.ts";
import {
  DatasetSubmissionSteps,
  DataSetSubmissionTabularDataCellStyleVariantsMap,
  getActiveStepIdx,
  getDataSetSubmissionTabularCellError,
  getFailedRowsFromTransactionDetails,
  getReadableDataSetSubmissionErrorType,
  getReadableTransactionErrorDetails,
  getSourceTypeDisplayLabel,
  getStateDisplayLabel,
  getStatusDisplayLabel,
  getStepDisplayLabel,
  getTransactionDetailStatusDisplayLabel,
  getTransactionDetailStatusDisplayLabelBasedOnStep,
  getTransactionDetailStatusesBasedOnStep,
  getWhichCellStyleVariantForCellInDataSetSubmissionTabularRow,
  getWhichCellStyleVariantForRowInDataSetSubmissionTabularRow,
  TransactionSourceOptions,
  TransactionStatusOptions,
} from "./view/transactions/index.ts";
import { UserStatusOptions } from "./view/users/index.ts";
import {
  UpdateAttributesSystemActionPrimaryAttributesThatCanBeUpdated,
  WorkflowStatusOptions,
  WorkflowStepTypeOptions,
  WorkflowTaskPriorityIdToLabelMap,
  WorkflowTaskTypeOptions,
  WorkflowTemplateStatusOptions,
} from "./view/workflows/index.ts";

type ResourceControllersType = {
  view: Record<string, ReactNode>;
  logic: Record<string, any>;
};

const Forms = {
  view: {
    FormSubmissionFormController,
    FormSubmissionsSectionRepeater,
    CalculatedFieldEquationController,
  },
  logic: {
    areConditionsSatisfied,
    CalculatedFieldService,
    ConditionFunctions,
    ConditionOperators,
    DateTimeSubstitutions,
    FormCalculatedFieldService,
    findAnswerForQuestion,
    findSectionUsingName,
    FormSubmissionStatusOptions,
    getAnswersForSubmission,
    getQuestionAnswerPairsBySection,
    getParentQuestionName,
    getSmartDefaultValues,
    resolveQuestionConfiguration,
    useFieldNamesToDisable,
    getErrorCount,
    getValuePathFromQuestionPath,
    useGenerateFieldsToWatch,
    useGenerateLookupQueries,
    useProcessAttributeLookupQueries,
    useProcessCalculatedFields,
    useProcessConditionalOverrides,
    useResolvedFormSchema,
    useUserChangedFieldPaths,
    getValueForQuestionInputFromQuestionValue,
    useValidateLookupFieldValue,
    getPickListValuesWithConditionalOverrides,
  },
} satisfies ResourceControllersType;

const Files = {
  view: {},
  logic: {
    getSafeFileName,
    getSafeFileMimeType,
  },
} satisfies ResourceControllersType;

const Measurements = {
  view: {},
  logic: {
    NON_DISPLAY_UNITS,
    NON_PRECISION_UNITS,
    getDisplayedUnit,
    getPreciseUnit,
    MeasurementAggregatorOptions,
    PollutantsEmitted,
    PollutantsEmittedOptions,
  },
} satisfies ResourceControllersType;

const TemplatedConfigurations = {
  view: {},
  logic: {
    getResourceIdForResource,
  },
} satisfies ResourceControllersType;

const ReportingGroups = {
  view: {},
  logic: {
    sortReportingGroupsByPriority,
  },
} satisfies ResourceControllersType;

const Permissions = {
  view: {},
  logic: {
    AuthQueryOperator,
    createAuthorizationEvaluator,
  },
} satisfies ResourceControllersType;

const Facilities = {
  view: {
    FacilityStatusOptions,
  },
  logic: {},
} satisfies ResourceControllersType;

const Equipment = {
  view: {
    EquipmentStatusOptions,
  },
  logic: {},
} satisfies ResourceControllersType;

const Devices = {
  view: {
    DeviceStatusOptions,
  },
  logic: {},
} satisfies ResourceControllersType;

const Activities = {
  view: {
    ActivityActionOptions,
    ActivityResourceTypeOptions,
  },
  logic: {},
} satisfies ResourceControllersType;

const Transactions = {
  view: {
    getSourceTypeDisplayLabel,
    getStatusDisplayLabel,
    getStateDisplayLabel,
    getStepDisplayLabel,
    getActiveStepIdx,
    getTransactionDetailStatusDisplayLabelBasedOnStep,
    getTransactionDetailStatusDisplayLabel,
    getTransactionDetailStatusesBasedOnStep,
    getReadableDataSetSubmissionErrorType,
    DatasetSubmissionSteps,
    TransactionStatusOptions,
    TransactionSourceOptions,
    DataSetSubmissionTabularDataCellStyleVariantsMap,
    getWhichCellStyleVariantForCellInDataSetSubmissionTabularRow,
    getWhichCellStyleVariantForRowInDataSetSubmissionTabularRow,
    getDataSetSubmissionTabularCellError,
    getFailedRowsFromTransactionDetails,
    getReadableTransactionErrorDetails,
  },
  logic: {
    selectFirstAndLastTransactionState,
  },
} satisfies ResourceControllersType;

const Alerts = {
  view: {
    AlertOperatorOptions,
  },
  logic: {},
} satisfies ResourceControllersType;

const Workflows = {
  view: {
    WorkflowTaskTypeOptions,
    WorkflowStepTypeOptions,
    WorkflowTemplateStatusOptions,
    WorkflowStatusOptions,
    WorkflowTaskPriorityIdToLabelMap,
    UpdateAttributesSystemActionPrimaryAttributesThatCanBeUpdated,
  },
  logic: {
    flattenWorkflowSteps,
    getAllWorkflowSteps,
    validateAllStepIdsAreUnique,
  },
} satisfies ResourceControllersType;

const Assets = {
  view: {
    AssetTypeOptions,
    AssetTypeSelection,
  },
  logic: {},
} satisfies ResourceControllersType;

const Reports = {
  view: {
    ReportGeneratedStatusOptions,
  },
  logic: {},
} satisfies ResourceControllersType;

const Users = {
  view: {
    UserStatusOptions,
  },
  logic: {
    isValidereInternalUserEmail,
  },
} satisfies ResourceControllersType;

const Flows = {
  view: {
    FlowStatusOptions,
  },
  logic: {},
} satisfies ResourceControllersType;

const AssetGroups = {
  view: {
    AssetGroupStatusOptions,
    NetworkMeasurementTypeOptions,
  },
  logic: {},
} satisfies ResourceControllersType;

const TemplatedReports = {
  view: {
    getNameFormatReadable,
  },
  logic: {},
} satisfies ResourceControllersType;

const Events = {
  view: {
    EventStatusOptions,
  },
  logic: {},
} satisfies ResourceControllersType;

const Investigations = {
  view: {
    ApprovalDecisionDisplayLabel,
    ApprovalDecisionOptions,
    getApprovalDecisionDisplayLabel,
    getInvestigationStatusDisplayLabel,
    InvestigationStatusLabel,
    InvestigationStatusOptions,
    InvestigationSearchStatusOptions,
    PendingApprovalCardDefaultApproveLabel,
    PendingApprovalCardDefaultHeading,
    PendingApprovalCardDefaultRejectLabel,
    ToolInProgressTickerLabel,
    PendingApprovalCardUnknownPayloadMessage,
    InvestigationWriteApprovalDecisionUnavailableMessage,
    UnknownApprovalDecisionDisplayKey,
  },
  logic: {},
} satisfies ResourceControllersType;

const Insights = {
  view: {},
  logic: {
    getNumberOfUsersThatCanAccessQuicksightConsole,
  },
} satisfies ResourceControllersType;

export {
  Activities,
  Alerts,
  AssetGroups,
  Assets,
  convertMapToOpts,
  Devices,
  Equipment,
  Events,
  Facilities,
  FeatureFlags,
  Files,
  Flows,
  Forms,
  Insights,
  Investigations,
  Measurements,
  normalizeError,
  Permissions,
  PermissionsMap,
  ReportingGroups,
  Reports,
  TemplatedConfigurations,
  TemplatedReports,
  Transactions,
  usePrevious,
  Users,
  Workflows,
};
export {
  getValidWorkerVersionStatusTransition,
  resolveWorkerVersionComputeConfig,
  VALID_WORKER_VERSION_STATUS_TRANSITIONS,
} from "./logic/workers/index.ts";

export type {
  AssetTypeSelectionType,
  AuthQueryOperatorType,
  AuthQueryType,
  CalculatedFieldEquationControllerProps,
  ConditionFunctionTypes,
  DataSetSubmissionTabularDataRowType,
  FormSubmissionFormControllerType,
  QuestionConditionType,
  QuestionRendererPropType,
  QuestionRenderFunctionType,
  SectionRendererPropType,
  SectionRenderFunctionType,
  WatchedFields,
};
export type { WorkerVersionStatusTransitionMapType } from "./logic/workers/index.ts";
export type { ResolvedWorkerVersionComputeConfigType } from "./logic/workers/index.ts";
export type {
  DisplayedApprovalDecisionType,
  UnknownApprovalDecisionDisplayKeyType,
} from "./view/investigations/index.ts";
